import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import InsightsFeed from '../components/InsightsFeed.jsx';
import SnapPage from '../components/SnapPage.jsx';
import { api, publicInsights } from '../api/client.js';
import T from '../i18n/content.js';
import { useLang, useT } from '../i18n/LangContext.jsx';

const REQUEST_TIMEOUT_MS = 15000;
const noCachedData = () => null;

// Keep the last successful result during pagination/retries. A new loader is
// pending immediately, before its effect runs, so the old page stays disabled.
function useFeedRequest(load, getCached = noCachedData, timeoutMs = REQUEST_TIMEOUT_MS) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(() => ({
    data: getCached(), pending: true, error: false, request: load,
  }));

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
    setState(previous => ({
      ...previous,
      data: getCached() ?? previous.data,
      error: previous.request === load && previous.error,
      pending: true,
      request: load,
    }));

    load(controller.signal)
      .then(data => {
        if (active) setState({ data, pending: false, error: false, request: load });
      })
      .catch(error => {
        if (active) {
          console.warn('Insights feed request failed', { name: error?.name, status: error?.status });
          setState(previous => ({
            ...previous, pending: false, error: true, request: load,
          }));
        }
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [load, getCached, timeoutMs, attempt]);

  const retry = () => {
    setState(previous => ({ ...previous, pending: true }));
    setAttempt(previous => previous + 1);
  };

  return {
    data: state.request === load ? state.data : (getCached() ?? state.data),
    pending: state.pending || state.request !== load,
    error: state.error && state.request === load,
    retry,
  };
}

function InsightsContent({ lang }) {
  const t = useT();
  const S = T.insightsSec;
  const [requestedPage, setRequestedPage] = useState(1);
  // Shared article requests own their timeout; unmounting only ignores their
  // result here, allowing a prefetch or a later visit to reuse the same request.
  const loadNotes = useCallback(() => publicInsights.load(lang, requestedPage), [lang, requestedPage]);
  const cachedNotes = useCallback(() => publicInsights.peek(lang, requestedPage), [lang, requestedPage]);
  const loadMedia = useCallback(signal => api.mediums({ lang, signal }), [lang]);
  const notes = useFeedRequest(loadNotes, cachedNotes, 0);
  const media = useFeedRequest(loadMedia);

  // Reveal the heading and articles together. Media is independent and never
  // blocks the first render, including when it is slow, empty or unavailable.
  const initialPending = notes.data === null && notes.pending;
  if (initialPending) {
    return <div className="insights-pending" aria-busy="true" />;
  }

  if (notes.data === null) {
    return (
      <section className="section insights-request-error">
        <p role="alert">{t(S.articlesError)}</p>
        <button type="button" className="btn-ghost" onClick={notes.retry}>{t(S.retry)}</button>
      </section>
    );
  }

  return (
    <>
      <PageHeader title={t(S.pageTitle)} lede={t(S.lede)} motif="motif-insights.png" motifSize="lg" lower />
      <InsightsFeed
        notes={notes.data.data}
        notesPage={notes.data.page}
        totalNotesPages={notes.data.meta.total_pages || 1}
        notesPending={notes.pending}
        notesError={notes.error}
        onNotesPageChange={setRequestedPage}
        onNotesPagePrefetch={page => publicInsights.prefetch(lang, page)}
        onNotesRetry={notes.retry}
        media={media.data || []}
        mediaPending={media.pending}
        mediaError={media.error}
        onMediaRetry={media.retry}
      />
    </>
  );
}

export default function InsightsPage() {
  const { lang } = useLang();
  return (
    <SnapPage>
      {/* Reset page, tabs and requests synchronously when the language changes. */}
      <InsightsContent key={lang} lang={lang} />
    </SnapPage>
  );
}
